import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import customColors from '../../../utils/customColors';
import type { WidgetItemType } from '../Dashboard.types';

type SemanticColor = 'success' | 'error' | 'warning' | 'info';

const DashboardWidgetProgress = ({
	widgetContent,

	totalSum: _totalSum,
}: {
	widgetContent: WidgetItemType[];

	totalSum: number;
}) => {
	const theme = useTheme();
	const { t } = useTranslation();
	const getAggregateValue = (item: WidgetItemType): number =>
		item.Aggregates['CargoNeto.sum'] ??
		item.Aggregates['neto.sum'] ??
		item.Aggregates['Neto.sum'] ??
		0;

	const maxAbs = useMemo(() => {
		let max = 0;
		for (const item of widgetContent) {
			const raw =
				item.Aggregates['CargoNeto.sum'] ??
				item.Aggregates['neto.sum'] ??
				item.Aggregates['Neto.sum'] ??
				0;
			const v = Math.abs(raw);
			if (v > max) {
				max = v;
			}
		}
		return max;
	}, [widgetContent]);

	const colorMap = useMemo(() => {
		const colors: SemanticColor[] = ['success', 'error', 'warning', 'info'];
		const map = new Map<string, SemanticColor>();
		widgetContent.forEach((item, index) => {
			map.set(item.Key, colors[index % colors.length]);
		});
		return map;
	}, [widgetContent]);

	const getSemanticFillColor = (themeArg: Theme, key: SemanticColor): string =>
		themeArg.palette[key].main;

	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 10;
	const totalPages = Math.ceil(widgetContent.length / itemsPerPage);
	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;
	const currentItems = widgetContent.slice(startIndex, endIndex);

	const handlePreviousPage = () => {
		setCurrentPage((prev) => Math.max(prev - 1, 1));
	};

	const handleNextPage = () => {
		setCurrentPage((prev) => Math.min(prev + 1, totalPages));
	};

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: 2,
				overflow: 'auto',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: 2,
					pr: 2,
					overflow: 'auto',
				}}
			>
				{currentItems.map((item) => {
					const value = getAggregateValue(item);
					const semantic = colorMap.get(item.Key) ?? 'info';
					const fillColor = getSemanticFillColor(theme, semantic);
					const fillHalfRatio =
						maxAbs > 0 ? (Math.abs(value) / maxAbs) * 50 : 0;
					const signedPercentLabel =
						maxAbs > 0 ? ((value / maxAbs) * 100).toFixed(0) : '0';

					return (
						<Box key={item.Key}>
							<Box sx={{ display: 'flex', gap: '12px', mb: '4px' }}>
								<Typography sx={{ fontSize: 12, marginRight: 'auto' }}>
									{item.Key}
								</Typography>

								<Typography sx={{ fontSize: 12 }}>
									{value.toLocaleString()} t
								</Typography>

								<Typography sx={{ fontSize: 12, color: customColors.gray }}>
									{signedPercentLabel}%
								</Typography>
							</Box>

							<Box
								sx={{
									position: 'relative',
									height: 8,
									borderRadius: 4,
									bgcolor: (sxTheme) =>
										sxTheme.palette.mode === 'dark'
											? sxTheme.palette.grey[800]
											: sxTheme.palette.grey[200],
								}}
							>
								{value !== 0 && maxAbs > 0 && (
									<Box
										sx={{
											position: 'absolute',
											top: 0,
											bottom: 0,
											...(value > 0
												? {
														left: '50%',
														width: `${fillHalfRatio}%`,
														borderRadius: 4,
														borderTopLeftRadius: 0,
														borderBottomLeftRadius: 0,
													}
												: {
														left: `${50 - fillHalfRatio}%`,
														width: `${fillHalfRatio}%`,
														borderRadius: 4,
														borderTopRightRadius: 0,
														borderBottomRightRadius: 0,
													}),
											bgcolor: fillColor,
										}}
									/>
								)}
							</Box>
						</Box>
					);
				})}
			</Box>

			{totalPages > 1 && (
				<Stack
					direction="row"
					justifyContent="center"
					alignItems="center"
					spacing={2}
				>
					<Button
						variant="outlined"
						size="small"
						onClick={handlePreviousPage}
						disabled={currentPage === 1}
						sx={{ minWidth: '80px' }}
					>
						{t('global.pagination.previous')}
					</Button>
					<Typography
						variant="body2"
						sx={{ minWidth: '60px', textAlign: 'center' }}
					>
						{currentPage} / {totalPages}
					</Typography>
					<Button
						variant="outlined"
						size="small"
						onClick={handleNextPage}
						disabled={currentPage === totalPages}
						sx={{ minWidth: '80px' }}
					>
						{t('global.pagination.next')}
					</Button>
				</Stack>
			)}
		</Box>
	);
};

export default DashboardWidgetProgress;
